"use client"

import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import type { RefObject } from "react";
import Link from "next/link";
import { ColumnDef } from "@tanstack/react-table";
import { IconButton } from "@/components/icon-button";
import { XIcon } from "@/components/fun-ui/icons";
import { ResultViewMemo } from "../ResultView.memo";
import { PaperInformationMemo } from "../PaperInformation.memo";
import { SelectionHeader, SelectionCell } from "../SelectionCells";
import { Paper, Col } from "@/stores/slices/paperSlice";
import SortSelect from "../SortSelect";
import { CitationStyleHeaderSelect, useCitationStyleContext } from "../CitationStyleContext";
import { SupportedStyleKey, SupportedLang, LANG_NAME_MAP } from "@/hooks/useCiteproc";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-hot-toast";

const INFORMATION_ITEMS = [
  { name: "机构", key: "organization" },
  { name: "作者", key: "author" },
  { name: "年份", key: "createAt" },
  { name: "期刊", key: "journal" },
] as const;

function PaperCitation({ paper }: { paper: Paper }) {
  const {
    citeprocStyle,
    isCitationStyleReady,
    citeprocStyleLabel,
    citationSwitchEpoch,
    formatBibliography,
  } = useCitationStyleContext();

  const [expanded, setExpanded] = useState(false);
  const [html, setHtml] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [computedLabel, setComputedLabel] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const compute = useCallback(async () => {
    if (!paper.citeprocData || !formatBibliography) return;

    setComputedLabel(citeprocStyleLabel);
    setLoading(true);

    try {
      const rawLang = paper.citeprocData?.lang;
      const langText = rawLang ? String(rawLang) : '';
      const lang: SupportedLang = (langText === 'zh-CN' || langText === 'en-US')
        ? langText
        : (LANG_NAME_MAP as Record<string, SupportedLang>)[langText] ?? 'zh-CN';

      const result = await formatBibliography({
        style: citeprocStyle,
        lang,
        items: [{ ...paper.citeprocData, id: paper.paperId.toString() }],
      });

      const firstKey = Object.keys(result.map)[0];
      setHtml(firstKey ? result.map[firstKey] : '');
    } catch (e) {
      console.error('Format citation error:', e);
    } finally {
      setLoading(false);
    }
  }, [paper.citeprocData, paper.paperId, citeprocStyle, citeprocStyleLabel, formatBibliography]);

  useEffect(() => {
    setComputedLabel(citeprocStyleLabel);
  }, [citeprocStyleLabel]);

  useEffect(() => {
    if (!expanded || !isCitationStyleReady) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) {
        void compute();
      }
    };

    const idleId = typeof window.requestIdleCallback === "function"
      ? window.requestIdleCallback(run, { timeout: 3000 })
      : window.setTimeout(run, 0);

    return () => {
      cancelled = true;
      if (typeof idleId === "number") {
        window.clearTimeout(idleId);
      } else {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [expanded, isCitationStyleReady, citationSwitchEpoch, citeprocStyle, compute]);

  useEffect(() => {
    if (copied) {
      toast.success('复制成功');
    }
  }, [copied]);

  const handleCopy = useCallback(async () => {
    if (!html) return;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const plainText = (temp.textContent || temp.innerText || '').trim();
    if (!plainText) return;

    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
      }, 1200);
    } catch (error) {
      console.error('Copy citation failed:', error);
    }
  }, [html]);

  if (!paper.citeprocData) return null;

  return (
    <div className="mt-4 border-t border-dashed border-border pt-4">
      <div className="flex h-6 items-center leading-6">
        <button
          className="flex h-6 items-center gap-0.5 text-sm leading-6 text-annotation-foreground hover:text-foreground cursor-pointer transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            if (!expanded) {
              setExpanded(true);
            } else {
              setExpanded(false);
              setHtml('');
            }
          }}
        >
          <span>引用{computedLabel ? `（${computedLabel}）` : ''}</span>
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {expanded && (
          <button
            type="button"
            className="ml-4 inline-flex h-6 items-center rounded border border-border bg-background px-2 text-xs leading-6 text-annotation-foreground hover:bg-muted hover:text-muted-foreground focus:outline-none focus:ring-0"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          >
            复制
          </button>
        )}
      </div>
      {expanded && (
        <div className="mt-2 border-l-2 border-border pl-3 pr-2 text-sm text-annotation-foreground break-all">
          {loading ? (
            <span className="text-xs text-annotation-foreground">加载中...</span>
          ) : html ? (
            <div className="paperlist-csl-content" dangerouslySetInnerHTML={{ __html: html }} />
          ) : null}
        </div>
      )}
    </div>
  );
}

type UseTableColumnsParams = {
  cols: Col[];
  isSimplifyGrid: boolean;
  subjectId: string;
  getIsCurrentPageRowsSelected: (papers: Paper[]) => boolean | "indeterminate";
  toggleCurrentPageRowsSelected: (papers: Paper[], value: boolean) => void;
  getIsSelected: (paperId: string) => boolean;
  toggleSelected: (paperId: string, value: boolean) => void;
  setOrder: (order: any) => void;
  handleSetPaperCols: (colKeys: string[], cols: Col[], mode?: any) => Promise<void>;
  handleRetry: (paper: Paper, colKey: string) => void;
  handleRefresh: () => void;
  orderRef: RefObject<any>;
  isPolling?: boolean;
  hasCitationFeature?: boolean;
};

export function useTableColumns({
  cols,
  isSimplifyGrid,
  subjectId,
  getIsCurrentPageRowsSelected,
  toggleCurrentPageRowsSelected,
  getIsSelected,
  toggleSelected,
  setOrder,
  handleSetPaperCols,
  handleRetry,
  orderRef,
  isPolling = false,
  handleRefresh,
  hasCitationFeature,
}: UseTableColumnsParams): ColumnDef<Paper>[] {
  // 稳定化 header 函数，避免每次渲染都重新创建
  const selectHeader = useCallback(({ table }: { table: any }) => {
    const rows = table.getRowModel().rows;
    const papers = rows.map((row: any) => row.original);
    const checked = getIsCurrentPageRowsSelected(papers);
    return (
      <SelectionHeader
        papers={papers}
        checked={checked}
        onToggle={(value) => toggleCurrentPageRowsSelected(papers, value)}
      />
    );
  }, [getIsCurrentPageRowsSelected, toggleCurrentPageRowsSelected]);

  // 稳定化 cell 函数，避免每次渲染都重新创建
  const selectCell = useCallback(({ row }: { row: any }) => {
    const paperId = row.original.paperId;
    return (
      <SelectionCell
        paperId={paperId}
        checked={getIsSelected(paperId)}
        onToggle={(value) => toggleSelected(paperId, value)}
      />
    );
  }, [getIsSelected, toggleSelected]);

  // 稳定化 information header 函数
  const informationHeader = useCallback(() => {
    return (
      <div className="flex flex-row items-center justify-between">
        <div>文献</div>
        <SortSelect value={orderRef.current} onSortChange={setOrder} />
      </div>
    );
  }, [setOrder, orderRef]);

  return useMemo(() => {
    const staticCols: ColumnDef<Paper>[] = [
      {
        id: "select",
        header: selectHeader,
        cell: selectCell,
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "information",
        header: informationHeader,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <PaperInformationMemo
              className=""
              paper={paper}
              subjectId={subjectId}
            />
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
    ];

    const dynamicCols: ColumnDef<Paper>[] = cols
      .filter(({ selected }) => selected)
      .map((col) => {
        const { name, key } = col;
        return ({
          accessorKey: key,
          header: () => (
            <div className="min-w-[400px] flex items-center justify-between">
              <span>{name}</span>
              <IconButton
                label="关闭当前表格列"
                className="text-annotation-foreground"
                onClick={() => {
                  handleSetPaperCols([key], [col], 'del');
                }}
                Icon={XIcon}
              />
            </div>
          ),
          cell: ({ row }) => {
            const paper = row.original as Paper;
            const colData = paper.understands?.[key];
            return (
              <ResultViewMemo
                key={row.id}
                keyName={key}
                paperName={paper.paperName}
                taskId={colData?.taskId}
                result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : colData?.result}
                images={colData?.images}
                tables={colData?.tables}
                status={colData?.status}
                extractStatus={paper?.extractStatus}
                onRetry={() => handleRetry(paper, key)}
                onRefresh={handleRefresh}
                isPolling={isPolling}
                attachmentId={paper.attachmentId}
              />
            );
          },
        });
      });

    const informationCols: ColumnDef<Paper>[] = INFORMATION_ITEMS.map(({ name, key }) => ({
      id: `information.result[${key}]`,
      accessorKey: `information.result[${key}]`,
      header: () => <div className="min-w-[260px]">{name}</div>,
      cell: ({ row }) => {
        const paper = row.original as Paper;
        const { result, status } = paper.information;
        return (
          <ResultViewMemo
            key={row.id}
            keyName={key}
            taskId={paper.paperId}
            paperName={paper.paperName}
            result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : (result[(key as keyof typeof result)] as string)}
            status={status}
            extractStatus={paper?.extractStatus}
            onRetry={() => handleRetry(paper, key)}
            onRefresh={handleRefresh}
            isPolling={isPolling}
            className="min-w-[260px]"
            attachmentId={paper.attachmentId}
          />
        )
      },
      enableHiding: false,
      enablePinning: true,
    }));

    const informationCol: ColumnDef<Paper>[] = [{
      name: "基础信息",
      key: "information",
    } as any].map(({ name, key }) => ({
      id: `information.result[${key}]`,
      accessorKey: `information.result[${key}]`,
      header: () => (
        <div className="min-w-[260px] flex items-center justify-between gap-2">
          <span>{name}</span>
          {hasCitationFeature ? <CitationStyleHeaderSelect /> : null}
        </div>
      ),
      cell: ({ row }) => {
        const paper = row.original as Paper;
        const { result, status } = paper.information;
        let label = INFORMATION_ITEMS.map((item) => {
          return `**${item.name}**：${result[(item.key as keyof typeof result)]}`;
        }).join('\n\n');
        if (status !== 2) {
          label = '';
        }
        return (
          <div>
            <ResultViewMemo
              key={row.id}
              keyName={key}
              taskId={paper.paperId}
              paperName={paper.paperName}
              result={(paper?.extractStatus && paper?.extractStatus < 0) ? paper?.error : label}
              status={status}
              extractStatus={paper?.extractStatus}
              onRetry={() => handleRetry(paper, key)}
              onRefresh={handleRefresh}
              isPolling={isPolling}
              className="min-w-[260px]"
              attachmentId={paper.attachmentId}
            />
            {hasCitationFeature
              && paper.citeprocData
              && status === 2
              && !!label
              && !(paper?.extractStatus && paper?.extractStatus < 0) && (
              <PaperCitation paper={paper} />
            )}
          </div>
        )
      },
      enableHiding: false,
      enablePinning: true,
    }));

    const restCols: ColumnDef<Paper>[] = [
      {
        accessorKey: "currPage",
        header: () => <div className="min-w-[130px]">阅读进度</div>,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <div className="min-w-[130px]">
              {paper.totalPage ? Math.round(paper.currPage / paper.totalPage * 100) : 0}%
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
      {
        accessorKey: "noteCount",
        header: () => <div className="min-w-[130px]">笔记摘录</div>,
        cell: ({ row }) => {
          const paper = row.original as Paper;
          return (
            <div className="min-w-[130px]">
              <Link className="text-primary hover:underline" href={`/papers/${paper.paperId}?to=list`}>{paper.noteCount}</Link>
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
      {
        accessorKey: "createTime",
        header: () => <div className="min-w-[180px]">上传时间</div>,
        cell: ({ getValue }) => {
          return (
            <div className="min-w-[180px]">
              {getValue<string>()}
            </div>
          );
        },
        enableHiding: false,
        enablePinning: true,
      },
    ];

    return isSimplifyGrid
      ? staticCols.concat(informationCols).concat(restCols)
      : staticCols.concat(informationCol).concat(dynamicCols);
  }, [
    cols,
    isSimplifyGrid,
    subjectId,
    handleSetPaperCols,
    handleRetry,
    handleRefresh,
    isPolling,
    selectHeader,
    selectCell,
    informationHeader,
    hasCitationFeature,
  ]);
}

